import { RecoveryFlow, UpdateRecoveryFlowBody } from "@ory/client";
import axios from "axios";
import Head from "next/head";
import { useRouter } from "next/router";
import { useEffect, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { FullScreenLoader } from "~/components/full-screen-loader";
import { AuthLayout } from "~/features/auth";
import { useOry } from "~/hooks/use-ory";
import {
  analytics,
  ButtonClickEvent,
  ScreenShownEvent,
  trackFlow,
} from "~/libs/analytics";
import { pathWithQuery } from "~/libs/http";
import { handleFlowError, setFormValues, setUriFlow } from "~/libs/ory";
import {
  Box,
  Button,
  Error,
  FormField,
  Heading,
  InputField,
  Alert,
  Text,
} from "~/ui/components";
import { FormIntro } from "~/ui/components/form-intro";

import { FlowMessage } from "./components/flow-message";

type FormData = {
  csrf_token: string;
  code: string;
};

const ForgotPassword = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const ory = useOry();
  const form = useForm<FormData>();
  const [busy, busySet] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = form;

  const [identifier, setIdentifier] = useState<string>();
  const [step, setStep] = useState<"default" | "verify-code">("default");
  const [flow, setFlow] = useState<RecoveryFlow>();

  const { flow: flowId, return_to: returnTo } = router.query;

  const onSubmitForgotPassword = async (data: FormData) => {
    if (!flow) return;

    await setUriFlow(router, flow.id);

    const body = {
      csrf_token: data.csrf_token,
      email: identifier,
      method: "code",
    } as UpdateRecoveryFlowBody;

    try {
      busySet(true);

      analytics.track(
        new ButtonClickEvent({
          button: "send code",
          screen: "pw-reset: start",
        }),
      );

      const { data } = await ory.updateRecoveryFlow({
        flow: flow.id,
        updateRecoveryFlowBody: body,
      });

      setFlow(data);
      trackFlow({ flow: data, screen: "pw-reset: start" });
      data.state === "sent_email" && setStep("verify-code");

      analytics.track(
        new ScreenShownEvent({
          screen: "pw-reset: code input",
        }),
      );
    } catch (error) {
      if (!axios.isAxiosError(error)) throw error;
      trackFlow({ flow: error.response?.data, screen: "pw-reset: start" });
      await handleFlowError(router, "recovery")(error);
    } finally {
      busySet(false);
    }
  };

  const onSubmitVerifyCode = async (data: FormData) => {
    if (!flow) return;

    await setUriFlow(router, flow.id);

    const body = {
      csrf_token: data.csrf_token,
      code: data.code,
      method: "code",
    } as UpdateRecoveryFlowBody;

    try {
      busySet(true);

      analytics.track(
        new ButtonClickEvent({
          button: "verify code",
          screen: "pw-reset: code input",
        }),
      );

      const { data } = await ory.updateRecoveryFlow({
        flow: flow.id,
        updateRecoveryFlowBody: body,
      });

      setFlow(data);
      trackFlow({ flow: data, screen: "pw-reset: code input" });
    } catch (error) {
      if (!axios.isAxiosError(error)) throw error;
      trackFlow({ flow: error.response?.data, screen: "pw-reset: code input" });

      switch (error.response?.data?.error?.id) {
        case "browser_location_change_required":
          router.push(
            pathWithQuery(error.response.data.redirect_browser_to, {
              source: "forgot-password",
            }),
          );
          return;
      }

      await handleFlowError(router, "recovery")(error);
    } finally {
      busySet(false);
    }
  };

  useEffect(() => {
    const recoveryFlow = async () => {
      if (!router.isReady || flow) {
        return;
      }

      if (flowId) {
        try {
          const { data } = await ory.getRecoveryFlow({ id: String(flowId) });
          setFlow(data);
          setFormValues(form, data);
          trackFlow({ flow: data, screen: "pw-reset: start" });
        } catch (error) {
          if (!axios.isAxiosError(error)) throw error;
          trackFlow({ flow: error.response?.data, screen: "pw-reset: start" });
          await handleFlowError(router, "recovery")(error);
        }
        return;
      }

      try {
        const { data } = await ory.createBrowserRecoveryFlow();
        setFlow(data);
        setFormValues(form, data);
        trackFlow({ flow: data, screen: "pw-reset: start" });
      } catch (error) {
        if (!axios.isAxiosError(error)) throw error;
        trackFlow({ flow: error.response?.data, screen: "pw-reset: start" });
        await handleFlowError(router, "recovery")(error);
      }
    };

    recoveryFlow();
  }, [router, router.isReady, flow, flowId, returnTo, ory, form]);

  useEffect(() => {
    if (!router.isReady) return;

    const { identifier } = router.query;

    if (!identifier || typeof identifier !== "string") {
      router.push("/login");
      return;
    }

    setIdentifier(identifier);
  }, [router, router.isReady]);

  const screenShown = useCallback(() => {
    analytics.track(
      new ScreenShownEvent({
        screen: "pw-reset: start",
      }),
    );
  }, []);

  useEffect(() => screenShown(), [screenShown]);

  if (!flow) {
    return <FullScreenLoader />;
  }

  return (
    <>
      <Head>
        <title>{t("forgotPassword.title")}</title>
      </Head>
      <AuthLayout>
        {step === "default" && (
          <Box space="large" textCenter>
            <FormIntro title={t("forgotPassword.heading")} />
            <FlowMessage flow={flow}></FlowMessage>
            <Text>
              {t("forgotPassword.introductory[0]")}{" "}
              <Text variant="primary" as="span">
                {identifier}
              </Text>{" "}
              {t("forgotPassword.introductory[1]")}
            </Text>
            <form onSubmit={handleSubmit(onSubmitForgotPassword)}>
              <input type="hidden" {...register("csrf_token")} />

              <Button loading={busy} fullWidth>
                Code senden
              </Button>
            </form>
          </Box>
        )}

        {step === "verify-code" && (
          <form onSubmit={handleSubmit(onSubmitVerifyCode)}>
            <input type="hidden" {...register("csrf_token")} />

            <Box space="large" textCenter>
              <FormIntro
                title={t("resetPassword.heading")}
                intro={[
                  t("resetPassword.introductory"),
                  " ",
                  <Text variant="primary" as="span">
                    {identifier}
                  </Text>,
                  ".",
                ]}
              />

              <FlowMessage flow={flow}></FlowMessage>

              {(identifier || "").includes("@outlook.com") || (identifier || "").includes("@hotmail.com") ? <b><Alert text="E-Mails an Microsoft-Konten (z.B. Outlook, Hotmail) landen möglicherweise fälschlicherweise in Ihrem Spam-Ordner. Bitte prüfen Sie diesen, falls Sie unsere E-Mail nicht erhalten."></Alert></b> : <></> }

              <FormField>
                <InputField
                  autoFocus
                  autoComplete="one-time-code"
                  label={t<string>("resetPassword.code")}
                  {...register("code")}
                />
                {errors.code && <Error>{errors.code.message}</Error>}
              </FormField>
              <Button loading={busy} fullWidth>
                {t("common.continue")}
              </Button>
            </Box>
          </form>
        )}
      </AuthLayout>
    </>
  );
};

export { ForgotPassword };
